import React, { useEffect, useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  IconButton,
  Stack,
  Chip,
  capitalize,
  Autocomplete,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  InputAdornment,
  Collapse,
} from '@mui/material';
import { Add, Check, Close, RemoveRedEyeOutlined, Search, FilterList, Clear } from '@mui/icons-material';
import { StyledCellHeader } from '../../../../components/StyledCellHeader';
import { formatNumberWithCommas } from '../../../../utils/generalFunctions';
import { useNavigate } from 'react-router-dom';
import OnGeneralMessageDialog from '../../../../components/Dialogs/OnGeneralMessageDialog';
import AlertMessageDialog from '../../../../components/Dialogs/alertMessageDialog';
import CobrarInicialMainDialog from './cobrarinicialmaindialog';
import DetalleInicialDialog from './detalleinicial';

import AccessDeniedBox from '../../../../components/AccessDeniedBox';


function EstadoChip({ estado }) {
  let color = 'default';
  if (estado === 'utilizado') color = 'success';
  else if (estado === 'pendiente') color = 'warning';
  else if (estado === 'cancelado') color = 'error';
  return <Chip label={capitalize(estado)} color={color} textTransform="none" size="small"/>;
}

const Iniciales = () => {
  const navigate = useNavigate();
  const [iniciales, setIniciales] = useState([]);
  const [inicialesFiltered, setInicialesFiltered] = useState([]);
  const [openGeneralAcceptMessageDialog, setOpenGeneralAcceptMessageDialog] = useState(false);
  const [openGeneralRejectMessageDialog, setOpenGeneralRejectMessageDialog] = useState(false);
  const [solicitudSeleccionada, setSolicitudSeleccionada] = useState(null);
  const [openAlert, setOpenAlert] = useState(false);
  const [search, setSearch] = useState("");
  const [openCobrarInicialDialog, setOpenCobrarInicialDialog] = useState(false);
  const [openDetalleDialog, setOpenDetalleDialog] = useState(false);
  const [inicialSeleccionado, setInicialSeleccionado] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    estado: '',
    sucursal: '',
    montoMin: '',
    montoMax: '',
    fechaDesde: '',
    fechaHasta: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [sucursales, setSucursales] = useState([]);
  const [accessDenied, setAccessDenied] = useState(false);
  const handleInputChange = (val) => {
    if(val != null) {
      setInicialesFiltered(Array.isArray(iniciales) ? iniciales.filter(inicial => inicial.id==val.id) : []);
    } else {
      setInicialesFiltered(iniciales);
    }
  }

  useEffect(() => {
      const fetchIniciales = async () => {
            const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/iniciales/${localStorage.getItem('idcompany')}`,{
              headers: {
                  'Content-Type': 'application/json',
                  'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
              }
          });

          const data = await response.json();
          if(data["error"]!="Acceso denegado"){
            setAccessDenied(false);
            setIniciales(Array.isArray(data) ? data : []);
            setInicialesFiltered(data);
            
            // Extract unique sucursales for filter
            const uniqueSucursales = [...new Set(data?.length > 0 ? data.map(item => item.sucursal_nombre).filter(Boolean) : [])];
            setSucursales(uniqueSucursales);
          } else {
            setAccessDenied(true);
          }
      };
      fetchIniciales();
  }, []);

  const handleCobrarInicial = (data) => {
    setOpenAlert(true);
  }

  const handleViewDetails = (inicial) => {
    setInicialSeleccionado(inicial);
    setOpenDetalleDialog(true);
  }

  const handleCloseDetalleDialog = () => {
    setOpenDetalleDialog(false);
    setInicialSeleccionado(null);
  }

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  }

  const applyFilters = () => {
    let filtered = [...iniciales];

    // Search filter
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(inicial => 
        inicial.identificacion?.toLowerCase().includes(searchTerm) ||
        inicial.nombre_cliente?.toLowerCase().includes(searchTerm) ||
        inicial.sucursal_nombre?.toLowerCase().includes(searchTerm)
      );
    }

    // Estado filter
    if (filters.estado) {
      filtered = filtered.filter(inicial => inicial.estado === filters.estado);
    }

    // Sucursal filter
    if (filters.sucursal) {
      filtered = filtered.filter(inicial => inicial.sucursal_nombre === filters.sucursal);
    }

    // Monto filters
    if (filters.montoMin) {
      filtered = filtered.filter(inicial => parseFloat(inicial.monto_inicial) >= parseFloat(filters.montoMin));
    }
    if (filters.montoMax) {
      filtered = filtered.filter(inicial => parseFloat(inicial.monto_inicial) <= parseFloat(filters.montoMax));
    }

    // Date filters
    if (filters.fechaDesde) {
      filtered = filtered.filter(inicial => {
        const fechaInicial = new Date(inicial.fecha_creacion.split('/').reverse().join('-'));
        const fechaDesde = new Date(filters.fechaDesde);
        return fechaInicial >= fechaDesde;
      });
    }
    if (filters.fechaHasta) {
      filtered = filtered.filter(inicial => {
        const fechaInicial = new Date(inicial.fecha_creacion.split('/').reverse().join('-'));
        const fechaHasta = new Date(filters.fechaHasta);
        return fechaInicial <= fechaHasta;
      });
    }

    setInicialesFiltered(filtered);
  }

  const clearFilters = () => {
    setFilters({
      search: '',
      estado: '',
      sucursal: '',
      montoMin: '',
      montoMax: '',
      fechaDesde: '',
      fechaHasta: ''
    });
    setInicialesFiltered(Array.isArray(iniciales) ? iniciales : []);
  }

  // Apply filters when filters change
  useEffect(() => {
    applyFilters();
  }, [filters, iniciales]);

  const handleCancelInicial = async (inicial) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/iniciales/${inicial.id}/cancelar`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
        }
      });

      if (response.ok) {
        // Refresh the data after successful cancellation
        const fetchIniciales = async () => {
          const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/iniciales/${localStorage.getItem('idcompany')}`, {
            headers: {
              'Content-Type': 'application/json',
              'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
            }
          });
          const data = await response.json();
          setIniciales(Array.isArray(data) ? data : []);
          setInicialesFiltered(data);
        };
        await fetchIniciales();
        
        // Close the detail dialog
        setOpenDetalleDialog(false);
        setInicialSeleccionado(null);
        
        // Show success message
        setOpenAlert(true);
      } else {
        const errorData = await response.json();
        console.error('Error canceling inicial:', errorData);
        // You could add error handling here if needed
      }
    } catch (error) {
      console.error('Error canceling inicial:', error);
      // You could add error handling here if needed
    }
  }



  return (
    !accessDenied ? 
    <Box sx={{ p: 3 , display:"flex", flexDirection:"column", alignItems:"flex-start"}}>
     <AlertMessageDialog message={`El inicial se ha cancelado exitosamente.`} title={''} severity={"success"} color={"success"} open={openAlert} handleClose={()=>{setOpenAlert(false);setOpenCobrarInicialDialog(false)}}/>
     <Stack direction="row" spacing={2} sx={{ mb: 3, width: '100%', alignItems: 'center' }}>
       <Button variant="contained" color='primarybutton' startIcon={<Add/>} sx={{width:200,fontWeight:"bold"}} onClick={()=>{setOpenCobrarInicialDialog(true)}}>Cobrar Inicial</Button>
       
       <Button 
         variant="outlined" 
         startIcon={<FilterList />}
         onClick={() => setShowFilters(!showFilters)}
         sx={{ fontWeight: "bold" }}
       >
         Filtros
       </Button>
       
       <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
         {inicialesFiltered.length} de {iniciales.length} iniciales
       </Typography>
     </Stack>
     {openCobrarInicialDialog && (
      <CobrarInicialMainDialog open={openCobrarInicialDialog} onClose={()=>{setOpenCobrarInicialDialog(false)}} onConfirm={handleCobrarInicial} />
     )}
     {openDetalleDialog && (
      <DetalleInicialDialog 
        open={openDetalleDialog} 
        onClose={handleCloseDetalleDialog} 
        inicial={inicialSeleccionado}
        onCancelPayment={handleCancelInicial}
      />
     )}

     {/* Filter Panel */}
     <Collapse in={showFilters} sx={{ width: '100%', mb: 2 }}>
       <Card sx={{ mb: 2 }}>
         <CardContent>
           <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
             <FilterList color="primary" />
             Filtros de Búsqueda
           </Typography>
           
           <Grid container spacing={2}>
          
             {/* Estado Filter */}
             <Grid item size={4}>
               <Box>
                 <Typography variant="body2" color="text.secondary" sx={{ fontWeight: "bold", textAlign: 'left' }}>
                   Estado
                 </Typography>
                 <FormControl fullWidth>
                   <Select
                     value={filters.estado}
                     onChange={(e) => handleFilterChange('estado', e.target.value)}
                     sx={{backgroundColor: 'white'}}
                     displayEmpty
                   >
                     <MenuItem value="">Todos</MenuItem>
                     <MenuItem value="pendiente">Pendiente</MenuItem>
                     <MenuItem value="utilizado">Utilizado</MenuItem>
                     <MenuItem value="cancelado">Cancelado</MenuItem>
                   </Select>
                 </FormControl>
               </Box>
             </Grid>

             {/* Sucursal Filter */}
             <Grid item size={4}>
               <Box>
                 <Typography variant="body2" color="text.secondary" sx={{ fontWeight: "bold", textAlign: 'left' }}>
                   Sucursal
                 </Typography>
                 <FormControl fullWidth>
                   <Select
                     value={filters.sucursal}
                     onChange={(e) => handleFilterChange('sucursal', e.target.value)}
                     sx={{backgroundColor: 'white'}}
                     displayEmpty
                   >
                     <MenuItem value="">Todas</MenuItem>
                     {sucursales.map((sucursal) => (
                       <MenuItem key={sucursal} value={sucursal}>
                         {sucursal}
                       </MenuItem>
                     ))}
                   </Select>
                 </FormControl>
               </Box>
             </Grid>

             {/* Monto Min */}
             <Grid item size={3}>
               <Box>
                 <Typography variant="body2" color="text.secondary" sx={{ fontWeight: "bold", textAlign: 'left' }}>
                   Monto Mínimo
                 </Typography>
                 <TextField
                   fullWidth
                   type="number"
                   placeholder="0"
                   value={filters.montoMin}
                   onChange={(e) => handleFilterChange('montoMin', e.target.value)}
                   sx={{backgroundColor: 'white'}}
                   InputProps={{
                     startAdornment: <InputAdornment position="start">RD$</InputAdornment>,
                   }}
                 />
               </Box>
             </Grid>

             {/* Monto Max */}
             <Grid item size={3}>
               <Box>
                 <Typography variant="body2" color="text.secondary" sx={{ fontWeight: "bold", textAlign: 'left' }}>
                   Monto Máximo
                 </Typography>
                 <TextField
                   fullWidth
                   type="number"
                   placeholder="999999"
                   value={filters.montoMax}
                   onChange={(e) => handleFilterChange('montoMax', e.target.value)}
                   sx={{backgroundColor: 'white'}}
                   InputProps={{
                     startAdornment: <InputAdornment position="start">RD$</InputAdornment>,
                   }}
                 />
               </Box>
             </Grid>

             {/* Fecha Desde */}
             <Grid item size={3}>
               <Box>
                 <Typography variant="body2" color="text.secondary" sx={{ fontWeight: "bold", textAlign: 'left' }}>
                   Fecha Desde
                 </Typography>
                 <TextField
                   fullWidth
                   type="date"
                   value={filters.fechaDesde}
                   onChange={(e) => handleFilterChange('fechaDesde', e.target.value)}
                   sx={{backgroundColor: 'white'}}
                 />
               </Box>
             </Grid>

             {/* Fecha Hasta */}
             <Grid item size={3}>
               <Box>
                 <Typography variant="body2" color="text.secondary" sx={{ fontWeight: "bold", textAlign: 'left' }}>
                   Fecha Hasta
                 </Typography>
                 <TextField
                   fullWidth
                   type="date"
                   value={filters.fechaHasta}
                   onChange={(e) => handleFilterChange('fechaHasta', e.target.value)}
                   sx={{backgroundColor: 'white'}}
                 />
               </Box>
             </Grid>
           </Grid>

           {/* Filter Actions */}
           <Box sx={{ mt: 2, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
             <Button
               variant="outlined"
               startIcon={<Clear />}
               onClick={clearFilters}
               sx={{ fontWeight: "bold" }}
             >
               Limpiar Filtros
             </Button>
           </Box>
         </CardContent>
       </Card>
     </Collapse>

     {/* Quick Search Autocomplete */}
     <Autocomplete
       options={inicialesFiltered}
       getOptionLabel={(option) => {return option.identificacion+" - $"+formatNumberWithCommas(option.monto_inicial)+" - "+capitalize(option.estado)}}
       renderInput={(params) => <TextField {...params} placeholder="Búsqueda rápida por inicial específico" fullWidth/>}
       onChange={(e, val) => {handleInputChange(val)}}
       sx={{mb:2}}
       fullWidth
     />
      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none', mt:2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <StyledCellHeader sx={{textAlign: 'left'}}>Cliente</StyledCellHeader>
              <StyledCellHeader sx={{textAlign: 'center'}}>Monto Inicial</StyledCellHeader>
              <StyledCellHeader sx={{textAlign: 'center'}}>Producto</StyledCellHeader>
              <StyledCellHeader sx={{textAlign: 'center'}}>Estado</StyledCellHeader>
              <StyledCellHeader sx={{textAlign: 'center'}}>Sucursal</StyledCellHeader>
              <StyledCellHeader sx={{textAlign: 'center'}}>Fecha de Creacion</StyledCellHeader>
              <StyledCellHeader></StyledCellHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {inicialesFiltered?.length > 0 ? inicialesFiltered.map((inicial) => (
              <TableRow key={inicial.id}>
                <TableCell sx={{textAlign: 'left'}}>{inicial.identificacion}</TableCell>

                <TableCell sx={{textAlign: 'center'}}>${formatNumberWithCommas(inicial.monto_inicial)}</TableCell>
                <TableCell sx={{textAlign: 'center'}}>{inicial.id_producto}</TableCell>
                <TableCell sx={{textAlign: 'center'}}>
                  <EstadoChip estado={inicial.estado} />
                </TableCell>
                <TableCell sx={{textAlign: 'center'}}>{inicial.sucursal_nombre}</TableCell>
                <TableCell sx={{textAlign: 'center'}}>{inicial.fecha_creacion}</TableCell>
                <TableCell sx={{textAlign: 'center'}}>
                  <IconButton 
                    size="small" 
                    onClick={() => handleViewDetails(inicial)}
                    sx={{
                      color: 'primary.main',
                      '&:hover': {
                        backgroundColor: 'primary.light',
                        color: 'primary.dark'
                      }
                    }}
                  >  
                    <RemoveRedEyeOutlined />
                  </IconButton>
                </TableCell>
              </TableRow>
            )) : []}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
    :
    <AccessDeniedBox
      title={'Acceso denegado'}
      message={'No tienes permisos para ver esta sección de Iniciales. Contacta a tu administrador para solicitar acceso o verifica tus roles y permisos.'}
    />
  );
};

export default Iniciales;
